import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Attach Bearer token from localStorage to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 -> clear and redirect to login
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem("auth_token");
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  },
);

export const formatApiError = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Terjadi kesalahan";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(", ");
  return String(d);
};

// Auth
export const apiLogin = (username, password) =>
  api.post("/auth/login", { username, password }).then((r) => r.data);
export const apiMe = () => api.get("/auth/me").then((r) => r.data);

// Master
export const fetchKategori = (penjamin) =>
  api.get("/master/kategori", { params: penjamin ? { penjamin } : {} }).then((r) => r.data);
export const fetchKelas = () => api.get("/master/kelas").then((r) => r.data);
export const fetchKsmList = () => api.get("/master/ksm").then((r) => r.data);
export const fetchDokter = (ksm) =>
  api.get("/master/dokter", { params: ksm ? { ksm } : {} }).then((r) => r.data);
export const fetchInstrumen = (penjamin) =>
  api.get("/master/instrumen", { params: penjamin ? { penjamin } : {} }).then((r) => r.data);
export const fetchTarifMatriks = () => api.get("/master/tarif-matriks").then((r) => r.data);

// Master CRUD (admin)
export const createInstrumen = (row, penjamin) =>
  api.post("/master/instrumen", row, { params: penjamin ? { penjamin } : {} }).then((r) => r.data);
export const updateInstrumen = (detail, row, penjamin) =>
  api.put(`/master/instrumen/${encodeURIComponent(detail)}`, row, {
    params: penjamin ? { penjamin } : {},
  }).then((r) => r.data);
export const deleteInstrumen = (detail, penjamin) =>
  api.delete(`/master/instrumen/${encodeURIComponent(detail)}`, {
    params: penjamin ? { penjamin } : {},
  }).then((r) => r.data);

export const createDokter = (row) => api.post("/master/dokter", row).then((r) => r.data);
export const updateDokter = (nama, row) =>
  api.put(`/master/dokter/${encodeURIComponent(nama)}`, row).then((r) => r.data);
export const deleteDokter = (nama) =>
  api.delete(`/master/dokter/${encodeURIComponent(nama)}`).then((r) => r.data);

export const updateMatriks = (kondisi, row) =>
  api.put(`/master/tarif-matriks/${encodeURIComponent(kondisi)}`, row).then((r) => r.data);

// Master Excel export/import
export const exportMasterExcel = async () => {
  const res = await api.get("/master/export-excel", { responseType: "blob" });
  const blob = new Blob([res.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `master_backup_${new Date().toISOString().slice(0, 10)}.xlsx`;
  // Anchor must be attached to DOM for .click() to work in Firefox/Safari
  document.body.appendChild(a);
  a.click();
  // Revoke after a delay so the browser has time to initiate the download
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 200);
};

export const importMasterExcel = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api
    .post("/master/import-excel", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);
};

// Users CRUD (admin)
export const listUsers = () => api.get("/users").then((r) => r.data);
export const createUser = (payload) => api.post("/users", payload).then((r) => r.data);
export const updateUser = (username, payload) =>
  api.put(`/users/${encodeURIComponent(username)}`, payload).then((r) => r.data);
export const deleteUser = (username) =>
  api.delete(`/users/${encodeURIComponent(username)}`).then((r) => r.data);

// Hospital config
export const fetchHospitalConfig = () => api.get("/hospital-config").then((r) => r.data);
export const updateHospitalConfig = (cfg) =>
  api.put("/hospital-config", cfg).then((r) => r.data);

// Calculate / history
export const postCalculate = (payload) => api.post("/calculate", payload).then((r) => r.data);
export const saveCalculation = (payload) => api.post("/calculations", payload).then((r) => r.data);
export const updateCalculation = (id, payload) =>
  api.put(`/calculations/${id}`, payload).then((r) => r.data);
export const listCalculations = (search) =>
  api.get("/calculations", { params: search ? { search } : {} }).then((r) => r.data);
export const getCalculation = (id) => api.get(`/calculations/${id}`).then((r) => r.data);
export const deleteCalculation = (id) => api.delete(`/calculations/${id}`).then((r) => r.data);

// Share (auth to create; public to read)
export const createShareLink = (id) =>
  api.post(`/calculations/${id}/share`).then((r) => r.data);
export const fetchSharedCalculation = (token) =>
  api.get(`/share/${token}`).then((r) => r.data);
export const fetchPublicHospitalConfig = () =>
  api.get(`/share-config/hospital`).then((r) => r.data);

// Knowledge AI
export const listKnowledge = () => api.get("/knowledge").then((r) => r.data);
export const getKnowledge = (id) => api.get(`/knowledge/${id}`).then((r) => r.data);
export const deleteKnowledge = (id) => api.delete(`/knowledge/${id}`).then((r) => r.data);
const UPLOAD_CHUNK = 2 * 1024 * 1024;
export const uploadKnowledge = async (file, description, onProgress) => {
  const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const total = Math.max(1, Math.ceil(file.size / UPLOAD_CHUNK));
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("index", String(i));
    fd.append("chunk", file.slice(i * UPLOAD_CHUNK, (i + 1) * UPLOAD_CHUNK), file.name);
    await api.post("/knowledge/upload/chunk", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    onProgress && onProgress(Math.round(((i + 1) / total) * 90));
  }
  const res = await api.post("/knowledge/upload/complete", {
    upload_id: uploadId,
    filename: file.name,
    total_chunks: total,
    description: description || "",
  });
  onProgress && onProgress(100);
  return res.data;
};

// Mode AI
export const AI_DRAFT_KEY = "ai_calc_draft";
export const fetchAiModels = () => api.get("/ai/models").then((r) => r.data);
export const listAiSessions = () => api.get("/ai/sessions").then((r) => r.data);
export const createAiSession = (payload = {}) =>
  api.post("/ai/sessions", payload).then((r) => r.data);
export const getAiSession = (id) => api.get(`/ai/sessions/${id}`).then((r) => r.data);
export const deleteAiSession = (id) => api.delete(`/ai/sessions/${id}`).then((r) => r.data);
export const saveAiToHistory = (sessionId, payload) =>
  api.post(`/ai/sessions/${sessionId}/save-to-history`, payload).then((r) => r.data);
export const saveAiCustomTable = (sessionId, payload) =>
  api.post(`/ai/sessions/${sessionId}/save-custom`, payload).then((r) => r.data);

// SSE streaming chat (fetch, since axios cannot stream in browsers)
export const streamAiChat = async (sessionId, payload, handlers) => {
  const token = localStorage.getItem("auth_token");
  const res = await fetch(`${API}/ai/sessions/${sessionId}/chat`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
    } catch (_) {}
    throw new Error(detail);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const part of parts) {
      const line = part.split("\n").find((l) => l.startsWith("data: "));
      if (!line) continue;
      try {
        const ev = JSON.parse(line.slice(6));
        handlers[ev.type] && handlers[ev.type](ev);
      } catch (_) {}
    }
  }
};
